import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import AvailabilityCalendar from '../../AvailabilityCalendar';
import { numberWithSpaces } from '../../../utils/numbers';
import { parseToApiDate } from '../../../utils/react';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import { SchemaPossibleBookingDates } from '@repo/legacy-commerce-api';
import { PartiallyIntegratedUserDataEC } from '../../../reducers/app';

interface Props {
  intl: any;
  possibleBookingDates: SchemaPossibleBookingDates;
  userDataEC: PartiallyIntegratedUserDataEC;
  actionsJourney2: any;
  handleDateChange: any;
  product: any;
  dateRange: any;
  duration: any;
  setIsLoading?: any;
  showPriceBlock?: boolean;
  invalidDaysInRange?: number[];
  setInvalidDaysInRange?: any;
}

const AnalogConfiguration = ({
  intl,
  possibleBookingDates,
  userDataEC,
  actionsJourney2,
  handleDateChange,
  product,
  dateRange,
  duration,
  setIsLoading,
  showPriceBlock,
  invalidDaysInRange,
  setInvalidDaysInRange,
}: Props) => {
  const { ui } = userDataEC;

  const [price, setPrice] = useState<string | number>(0);
  const [performanceValue, setPerformanceValue] = useState<number>(null);
  const [numberOfWeeks, setNumberOfWeeks] = useState<number>(null);

  useEffect(() => {
    if (product && showPriceBlock) {
      if (product.priceinformation) {
        if (!product.priceinformation.standardprice) {
          setPrice(product.priceinformation.pricelabel);
        } else {
          setPrice(`${numberWithSpaces(product.priceinformation.standardprice)} CHF`);
        }

        if (product?.priceinformation?.pricedays) {
          setNumberOfWeeks(product?.priceinformation?.pricedays / 7);
        }
      }

      if (product.performancevalue) {
        setPerformanceValue(product.performancevalue);
      }
    }
  }, [product]);

  useEffect(() => {
    if (showPriceBlock && duration && product.producturn) {
      getNewPriceForAnalogProduct(dateRange, duration);
    }

    if (product && product.availabilities && product.availabilities.length > 0) {
      let newInvalidDays = [];
      if (dateRange) {
        for (let day of getDaysArray(dateRange[0].startDate, dateRange[0].endDate)) {
          const weekNo = getISOWeek(day);
          const year = getISOWeekYear(day);
          let weekNoAsString = String(weekNo);

          if (weekNo < 10) {
            weekNoAsString = `0${weekNo}`;
          }

          product.availabilities.forEach((availabilityObj) => {
            if (availabilityObj.year === String(year)) {
              if (availabilityObj?.week === weekNo || !(availabilityObj.weeks || []).includes(weekNoAsString)) {
                newInvalidDays.push(day);
              }
            }
          });
        }
      }
      setInvalidDaysInRange(newInvalidDays);
    } else {
      setInvalidDaysInRange([]);
    }
  }, [duration, dateRange, product.producturn]);

  const getDaysArray = function (start, end) {
    for (var arr = [], dt = new Date(start); dt <= new Date(end); dt.setDate(dt.getDate() + 1)) {
      arr.push(new Date(dt));
    }
    return arr;
  };

  const getNewPriceForAnalogProduct = (range, dayDuration) => {
    setIsLoading(true);
    actionsJourney2
      .getPriceForProductNoStateUpdate({
        producturn: product.producturn,
        startdate: parseToApiDate(dateRange[0].startDate),
        duration: dayDuration,
      })
      .then((res) => {
        setIsLoading(false);
        if (res.data.prices.length > 0) {
          setPrice(`${numberWithSpaces(res.data.prices[0].price)} CHF`);
          setNumberOfWeeks(Number(res.data.prices[0].days) / 7);
        }
        if (res.data && res.data.performancevalue_week) {
          setPerformanceValue(numberWithSpaces(res.data.performancevalue_week));
        } else {
          setPerformanceValue(null);
        }
      })
      .catch((err) => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <AvailabilityCalendar
        dateChangeCallback={handleDateChange}
        availabilities={product?.availabilities}
        possibleBookingDates={possibleBookingDates}
        dateRange={dateRange}
        duration={duration}
        minDays={product?.mindays}
        maxDays={product?.maxdays}
        isAnalogProduct={true}
        intl={intl}
        isMobile={ui.isMobile}
      />
      {invalidDaysInRange.length > 0 && (
        <div className='py-3 px-24 my-4 font-14 bg-yellow varied-warning'>
          <b className='me-2'>
            <FormattedMessage id='ec.modals.digiflex.variedAvailability' defaultMessage='Hinweis:' />
          </b>
          <FormattedMessage
            id='ec.modals.digiflex.variedAvailabilityText'
            defaultMessage='Nicht jeder Tag im ausgewählten Zeitraum ist verfügbar. Bitte überprüfen Sie Ihre Auswahl.'
          />
        </div>
      )}
      {showPriceBlock && (
        <div className={classNames('d-flex w-100 p-24 priceInfo', { 'flex-column': ui.isMobile })}>
          <div
            className={classNames('d-flex flex-column justify-content-start', {
              'w-30 pe-48': !ui.isMobile,
              'w-100 p-4': ui.isMobile,
            })}
          >
            <div className='font-16 font-weight-bold'>
              <FormattedMessage id='ec.modals.analog.price' defaultMessage='Preis' />
            </div>

            <div className='d-flex mt-3 flex-column align-items-start'>
              <div className='font-14 mt-2'>{price}</div>
            </div>
          </div>

          <div
            className={classNames('d-flex flex-column justify-content-start', {
              'w-33 px-48 border-start border-end': !ui.isMobile,
              'w-100 p-4': ui.isMobile,
            })}
          >
            <div className='font-16 font-weight-bold'>
              <FormattedMessage id='ec.modals.analog.duration' defaultMessage='Dauer' />
            </div>

            <div className='d-flex mt-3 flex-column align-items-start'>
              {numberOfWeeks && (
                <div className='font-14 mt-2'>
                  {numberOfWeeks} <FormattedMessage id='ec.modals.analog.week' defaultMessage='Woche(n)' />
                </div>
              )}
            </div>
          </div>

          {performanceValue && (
            <div
              className={classNames('d-flex flex-column justify-content-start', {
                'w-33 px-48': !ui.isMobile,
                'w-100 p-4': ui.isMobile,
              })}
            >
              <div className='font-16 font-weight-bold'>
                <FormattedMessage id='ec.modals.analog.contactOpportunities' defaultMessage='Kontaktchancen' />
              </div>

              <div className='d-flex mt-3 flex-column align-items-start'>
                <div className='font-14 mt-2'>
                  {numberWithSpaces(performanceValue)}{' '}
                  <FormattedMessage id='ec.modals.analog.perWeek' defaultMessage='Pro Woche' />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AnalogConfiguration;
